// Port of ios/Sources/Calls/CallSignalingSocket.swift (Android `CallSignalingSocket`).
//
// The SECOND Mattermost websocket a call runs on — deliberately not the worker's main socket, and
// not sharing its connection, because on this protocol the connection id IS the call session: the
// server admits `connID` to the call, addresses signaling to it, and drops the participant when it
// closes. Multiplexing onto the main socket would make every app-level reconnect a call drop. It
// lives on the PAGE, next to the RTCPeerConnection it feeds (a worker has none).
//
// Lifecycle: connect → (`authentication_challenge` only with a bearer token; the browser's session
// COOKIE authenticates the upgrade itself, same origin) → `hello` (connection id) → `join` → `_join`
// ack addressed to us → media starts. Pings every 5 s; a missed pong or transport error reconnects
// the SAME session (`reconnect` with the original conn id) inside the server's window, up to
// `reconnectWindowMillis`.
import { broadcastChannelId, CallsFrames, parseSocketMessage, type CallEmoji, type CallSocketEvent } from '../../../core/callsCodec';

export interface CallSignalingListener {
  /** The server accepted this client into the call. Media may start. */
  joined(): void;
  /** An SDP answer/offer or remote ICE candidate for the media session. */
  signal(json: string): void;
  /** The authoritative roster snapshot the server sends right after admission. */
  callState(callJson: string): void;
  rosterEvent(event: CallSocketEvent): void;
  /** The socket is gone for good — reconnection ran out or the server refused the join. */
  closed(reason: string | null): void;
  /** Diagnostics only. */
  log?(line: string): void;
}

export class CallSignalingSocket {
  static readonly pingIntervalMillis = 5_000;
  static readonly reconnectDelayMillis = 1_000;
  /** The server forgets the session after its own window; longer only delays telling the user. */
  static readonly reconnectWindowMillis = 20_000;

  private socket: WebSocket | null = null;
  private seq = 1;
  private connectionId: string | null = null;
  private originalConnectionId: string | null = null;
  private channelId: string | null = null;
  private joinedOnce = false;
  private closedByUs = false;
  private awaitingPongSeq: number | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private disconnectedAt = 0;
  /** Distinguishes transports so a stale socket's close can't kill the one that replaced it. */
  private generation = 0;

  constructor(
    private readonly url: () => string,
    private readonly token: () => string | null,
    private readonly listener: CallSignalingListener,
  ) {}

  /** This client's SESSION on the call — the FIRST connection id, which survives reconnects. */
  get sessionId(): string | null {
    return this.originalConnectionId;
  }

  connect(channelId: string): void {
    this.channelId = channelId;
    this.open(false);
  }

  /** Leaves the call and closes for good. Safe to call more than once (and from `pagehide`). */
  leave(): void {
    if (this.closedByUs) return;
    this.closedByUs = true;
    this.stopPings();
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState === WebSocket.OPEN && this.joinedOnce) {
      try {
        socket.send(CallsFrames.leave(this.nextSeq()));
      } catch {
        /* already going */
      }
    }
    try {
      socket?.close(1000);
    } catch {
      /* already closed */
    }
  }

  // MARK: - Sends (fire-and-forget; a frame for a dead transport is dropped like the natives do)

  /** Sends stay in order even though the SDP frame is compressed asynchronously: a candidate must
   *  never overtake the offer it belongs to (the natives get this from one serial queue). */
  private outbox: Promise<void> = Promise.resolve();

  private send(frame: string | Uint8Array | Promise<Uint8Array>): void {
    this.outbox = this.outbox.then(async () => this.sendNow(await frame)).catch(() => undefined);
  }

  private sendNow(frame: string | Uint8Array): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(frame as string);
    } catch {
      /* the transport is going; reconnection handles it */
    }
  }

  sendSdp(sdpJson: string): void {
    this.send(CallsFrames.sdp(this.nextSeq(), sdpJson));
  }
  sendIce(candidateJson: string): void {
    this.send(CallsFrames.ice(this.nextSeq(), candidateJson));
  }
  sendMute(muted: boolean): void {
    this.send(muted ? CallsFrames.mute(this.nextSeq()) : CallsFrames.unmute(this.nextSeq()));
  }
  sendScreenOn(streamId: string): void {
    this.send(CallsFrames.screenOn(this.nextSeq(), streamId));
  }
  sendScreenOff(): void {
    this.send(CallsFrames.screenOff(this.nextSeq()));
  }
  sendVideoOn(streamId: string): void {
    this.send(CallsFrames.videoOn(this.nextSeq(), streamId));
  }
  sendVideoOff(): void {
    this.send(CallsFrames.videoOff(this.nextSeq()));
  }
  sendRaiseHand(raised: boolean): void {
    this.send(raised ? CallsFrames.raiseHand(this.nextSeq()) : CallsFrames.unraiseHand(this.nextSeq()));
  }
  sendReaction(emoji: CallEmoji): void {
    this.send(CallsFrames.react(this.nextSeq(), emoji));
  }

  // MARK: - Connection

  private nextSeq(): number {
    return this.seq++;
  }

  private open(isReconnect: boolean): void {
    if (this.closedByUs) return;
    this.awaitingPongSeq = null;
    const generation = ++this.generation;
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url());
    } catch (error) {
      this.transportLost(String(error), generation);
      return;
    }
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    socket.onopen = () => {
      if (generation !== this.generation) return;
      const token = this.token();
      if (token) this.sendNow(CallsFrames.authChallenge(this.nextSeq(), token));
      this.startPings(socket, generation);
    };
    socket.onmessage = (e) => {
      if (generation !== this.generation || this.closedByUs) return;
      // The server never speaks binary to clients on this socket.
      if (typeof e.data === 'string') this.handle(e.data, isReconnect);
    };
    socket.onclose = (e) => this.transportLost(`closed ${e.code}`, generation);
    socket.onerror = () => undefined; // onclose follows
  }

  private handle(text: string, isReconnect: boolean): void {
    const event = parseSocketMessage(text, this.connectionId);
    switch (event.kind) {
      case 'hello': {
        this.connectionId = event.connectionId;
        if (this.originalConnectionId === null) this.originalConnectionId = event.connectionId;
        if (!this.channelId) return;
        const frame =
          isReconnect && this.originalConnectionId !== event.connectionId
            ? CallsFrames.reconnect(this.nextSeq(), this.channelId, this.originalConnectionId ?? event.connectionId, event.connectionId)
            : CallsFrames.join(this.nextSeq(), this.channelId);
        this.send(frame);
        this.listener.log?.(isReconnect ? 'socket resumed' : 'socket hello → join');
        // A reconnect resumes an admitted session: no second ack is coming, it simply carries on.
        if (isReconnect && this.joinedOnce) this.listener.joined();
        return;
      }
      case 'joinAck':
        this.joinedOnce = true;
        this.disconnectedAt = 0;
        this.listener.joined();
        return;
      case 'error':
        this.closedByUs = true;
        this.stopPings();
        try {
          this.socket?.close(1000);
        } catch {
          /* closing anyway */
        }
        this.listener.closed(event.message || 'join refused');
        return;
      case 'signal':
        this.listener.signal(event.json);
        return;
      case 'callState':
        this.listener.callState(event.callJson);
        return;
      case 'pong':
        if (event.seqReply === this.awaitingPongSeq) this.awaitingPongSeq = null;
        return;
      case 'ignored':
        return;
      default: {
        // Another channel's call is not this call (CallsCodec.broadcastChannelId).
        const eventChannel = broadcastChannelId(text);
        if (eventChannel && this.channelId && eventChannel !== this.channelId) return;
        this.listener.rosterEvent(event);
      }
    }
  }

  private startPings(socket: WebSocket, generation: number): void {
    this.stopPings();
    this.pingTimer = setInterval(() => {
      if (generation !== this.generation || this.closedByUs) return;
      if (this.awaitingPongSeq !== null) {
        // Open-looking but nothing coming back — treat it as lost rather than freezing the call.
        try {
          socket.close(4000);
        } catch {
          /* closing anyway */
        }
        this.transportLost('ping timeout', generation);
        return;
      }
      const pingSeq = this.nextSeq();
      this.awaitingPongSeq = pingSeq;
      this.send(CallsFrames.ping(pingSeq));
    }, CallSignalingSocket.pingIntervalMillis);
  }

  private stopPings(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private transportLost(reason: string, generation: number): void {
    if (this.closedByUs || generation !== this.generation) return;
    this.generation++; // the dead transport's late events are ignored from here on
    this.stopPings();
    this.socket = null;
    const now = Date.now();
    if (this.disconnectedAt === 0) this.disconnectedAt = now;
    this.listener.log?.(`socket lost: ${reason}`);
    if (now - this.disconnectedAt > CallSignalingSocket.reconnectWindowMillis) {
      this.closedByUs = true;
      this.listener.closed(reason || 'connection lost');
      return;
    }
    setTimeout(() => {
      if (!this.closedByUs) this.open(true);
    }, CallSignalingSocket.reconnectDelayMillis);
  }
}
